import { createContext, useState, useEffect, useContext } from 'react';

// Default settings
const DEFAULT_SETTINGS = {
  theme: 'light', // 'light' or 'dark'
  fontSize: 14,
  autoSave: true,
  renderOnChange: true,
  showLineNumbers: true,
};

// Create context
const SettingsContext = createContext();

// Custom hook for using the settings context
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Provider component
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Load settings from localStorage on component mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        const storedSettings = localStorage.getItem('mermaid-flow-settings');
        if (storedSettings) {
          setSettings(JSON.parse(storedSettings));
        } else {
          // Check if dark mode is enabled from environment variables
          const darkModeEnabled = import.meta.env.VITE_ENABLE_DARK_MODE === 'true';
          setSettings({
            ...DEFAULT_SETTINGS,
            theme: darkModeEnabled ? 'dark' : 'light',
          });
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        // Fallback to default settings
        setSettings(DEFAULT_SETTINGS);
      }
    };

    loadSettings();
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('mermaid-flow-settings', JSON.stringify(settings));
      
      // Apply theme to body
      if (settings.theme === 'dark') {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark-theme');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  }, [settings]);

  // Update settings
  const updateSettings = (newSettings) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      ...newSettings,
    }));
  };

  // Toggle theme
  const toggleTheme = () => {
    updateSettings({
      theme: settings.theme === 'light' ? 'dark' : 'light',
    });
  };

  // Reset settings to default
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  // Context value
  const value = {
    settings,
    updateSettings,
    toggleTheme,
    resetSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext; 